'use client';

/**
 * You, at the right end of the navbar: your avatar, and behind it everything
 * about you rather than about the page (owner, 2026-10-14: "the icon to be shown
 * as a user profile icon, and it should show a dropdown list … with lots of
 * features inside"). Reddit's user menu is the model.
 *
 *   you          avatar, nickname, role · View profile
 *   KHARMA       the balance, earned and spent; opens the history
 *   account      Edit profile · Notifications · KHARMA history · Co-op partners
 *                · Your Camps · Team activity (team leaders and up)
 *   preferences  Dark mode (a switch, right here) · Appearance and font · Flags
 *   help         Send feedback
 *   Sign out     last, apart, where nobody presses it on the way to anything else
 *
 * Every item opens its section of the account page, /account/<section>, whose
 * sidebar lists these same items in this same order: both draw them from
 * components/account/sections (owner 2026-10-16: pages, not dialogs). Signed
 * out, the same button offers the ways in, the free translations left, and the
 * preferences a guest has.
 *
 * WHY IT SOMETIMES GLOWS. For somebody the server still wants a nickname from
 * (identity's `unnamed`), the avatar wears a ring and the menu leads with
 * "Choose your nickname", until they answer. The settings gear used to carry
 * this cue; the gear now lives in here.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { useIdentity } from '@/lib/identity';
import { useTheme } from '@/lib/theme';
import { ROLE_LABEL } from '@/lib/roles';
import MarqCoin from '../MarqCoin';
import { GLYPH as G, accountHref, type AccountGroup, type AccountSection } from '../account/sections';
import { useVisibleSections } from '../account/AccountNav';

/** A face: the uploaded picture, or the first letter of the nickname on the coin's own amber. */
export function Face({ url, name, className }: { url: string | null; name: string; className: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [url]);
  if (url && !broken) {
    // eslint-disable-next-line @next/next/no-img-element -- a small, already-sized avatar
    return <img src={url} alt="" onError={() => setBroken(true)} className={className + ' rounded-full object-cover'} draggable={false} />;
  }
  return (
    <span aria-hidden className={className + ' grid place-items-center rounded-full bg-gradient-to-br from-amber-300 to-orange-500 font-bold uppercase text-ink-950'}>
      {name.slice(0, 1) || '?'}
    </span>
  );
}

const ITEM = 'flex min-h-[40px] w-full items-center gap-3 rounded-xl px-3 text-left text-[14px] text-ink-700 no-underline transition ' +
  'hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus-visible:bg-ink-100 dark:text-ink-200 dark:hover:bg-white/[0.07] dark:hover:text-white dark:focus-visible:bg-white/[0.07]';

function Glyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0 text-ink-500 dark:text-ink-400" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}


function Group({ children }: { children: ReactNode }) {
  return <div className="border-t border-ink-100 p-1.5 dark:border-white/[0.07]">{children}</div>;
}

/** Dark mode, switched in place: the menu stays open so the change can be seen. */
function DarkSwitch() {
  const { resolved, toggle } = useTheme();
  const on = resolved === 'dark';
  return (
    <button type="button" role="menuitemcheckbox" aria-checked={on} onClick={toggle} className={ITEM}>
      <Glyph d={G.moon} />
      <span className="flex-1">Dark mode</span>
      <span aria-hidden className={'relative h-[22px] w-[38px] shrink-0 rounded-full transition ' + (on ? 'bg-accent-600' : 'bg-ink-300 dark:bg-ink-600')}>
        <span className={'absolute top-[3px] h-4 w-4 rounded-full bg-white shadow transition-all ' + (on ? 'left-[19px]' : 'left-[3px]')} />
      </span>
    </button>
  );
}


export default function AccountMenu() {
  const { loaded, user, marq, guest, signOut } = useAuth();
  const { avatarUrl, unnamed: unnamedRaw, gateOpen } = useIdentity();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const visible = useVisibleSections();
  const wrap = useRef<HTMLDivElement>(null);
  const unnamed = !!user && unnamedRaw && !gateOpen;

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    // Capture, and stopped: inside a game Escape belongs to the game, and the
    // menu must take this one press for itself without the game seeing it.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    // A click inside a game's frame never reaches this document; losing focus to it is the only signal there is.
    const onBlur = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [open]);

  if (!loaded) return <span className="h-9 w-9 shrink-0 rounded-full bg-ink-100 dark:bg-white/[0.06]" aria-hidden />;

  const close = () => setOpen(false);
  const href = (id: AccountSection) => accountHref(id);
  /** A group's items, as links to their sections of the account page. */
  const items = (g: AccountGroup) => visible.filter((s) => s.group === g).map((s) => (
    <Link key={s.id} href={href(s.id)} role="menuitem" onClick={close} className={ITEM}><Glyph d={G[s.glyph]} />{s.label}</Link>
  ));
  const name = user?.nickname ?? '';
  const guestLeft = !user && guest && guest.total > 0 ? guest.remaining : null;

  return (
    <div ref={wrap} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user ? `Your account — ${name}${unnamed ? ' (choose your nickname)' : ''}` : 'Account and settings'}
        title={user ? name : 'Account and settings'}
        className={
          'relative grid h-9 w-9 place-items-center rounded-full ring-1 transition active:scale-95 ' +
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 ' +
          (open ? 'ring-2 ring-accent-500' : 'ring-ink-200 hover:ring-ink-400 dark:ring-white/20 dark:hover:ring-white/45')
        }
      >
        {user ? (
          <Face url={avatarUrl} name={name} className="h-8 w-8 text-[13px]" />
        ) : (
          <svg viewBox="0 0 24 24" className="h-[19px] w-[19px] text-ink-600 dark:text-ink-300" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="8.5" r="3.5" />
            <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
          </svg>
        )}
        {unnamed && <span className="gt-gear-ring pointer-events-none absolute inset-0 rounded-full border-2 border-accent-500" aria-hidden />}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Your account"
          className="fixed inset-x-2 top-16 z-[130] max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain rounded-2xl border border-ink-200 bg-white text-ink-900
                     shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] dark:border-white/10 dark:bg-ink-900 dark:text-ink-50
                     sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[300px]"
        >
          {user ? (
            <>
              <div className="flex items-center gap-3 px-4 pb-3 pt-4">
                <Face url={avatarUrl} name={name} className="h-12 w-12 shrink-0 text-[19px]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold">{name}</p>
                  <p className="truncate text-[12px] text-ink-500 dark:text-ink-400">
                    {ROLE_LABEL[user.role] ?? 'Member'}
                    {user.authProvider === 'reddit' && ' · Reddit account'}
                  </p>
                </div>
              </div>
              <div className="px-3 pb-3">
                <Link href={href('profile')} role="menuitem" onClick={close}
                      className="flex h-9 items-center justify-center rounded-full border border-ink-200 text-[13px] font-semibold text-ink-800 no-underline transition hover:bg-ink-50 dark:border-white/15 dark:text-ink-100 dark:hover:bg-white/[0.06]">
                  View profile
                </Link>
              </div>

              {unnamed && (
                <div className="px-3 pb-3">
                  <Link href={href('edit-profile')} role="menuitem" onClick={close}
                          className="block w-full rounded-xl bg-accent-50 px-3 py-2.5 text-left text-[13px] text-accent-800 transition hover:bg-accent-100 dark:bg-accent-500/15 dark:text-accent-200 dark:hover:bg-accent-500/25">
                    <span className="block font-semibold">Choose your nickname</span>
                    <span className="block text-[12px] opacity-80">You are still going by a name we made up.</span>
                  </Link>
                </div>
              )}

              {marq && (
                <Link href={href('history')} role="menuitem" onClick={close}
                        className="flex w-full items-center gap-3 border-t border-ink-100 px-4 py-3 text-left transition hover:bg-ink-50 dark:border-white/[0.07] dark:hover:bg-white/[0.05]">
                  <MarqCoin className="h-9 w-9 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[17px] font-bold tabular-nums leading-tight">{marq.balance.toLocaleString('en-US')} <span className="text-[12px] font-semibold text-ink-500 dark:text-ink-400">KHARMA</span></span>
                    <span className="block truncate text-[11.5px] text-ink-500 dark:text-ink-400">Earned {marq.earned.toLocaleString('en-US')} · spent {marq.spent.toLocaleString('en-US')}</span>
                  </span>
                </Link>
              )}

              <Group>{items('account')}</Group>
            </>
          ) : (
            <div className="px-4 pb-4 pt-4">
              <p className="text-[15px] font-bold">You are not signed in</p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-ink-500 dark:text-ink-400">
                An account keeps your KHARMA, your scores and your Camps, on every device.
                {guestLeft !== null && <> You have <strong className="text-ink-800 dark:text-ink-100">{guestLeft}</strong> free {guestLeft === 1 ? 'translation' : 'translations'} left.</>}
              </p>
              <div className="mt-3 flex gap-2">
                <a href="/login" className="flex h-9 flex-1 items-center justify-center rounded-full border border-ink-200 text-[13px] font-semibold text-ink-800 no-underline transition hover:bg-ink-50 dark:border-white/15 dark:text-ink-100 dark:hover:bg-white/[0.06]">Sign in</a>
                <a href="/register" className="flex h-9 flex-1 items-center justify-center rounded-full bg-accent-600 text-[13px] font-semibold text-white no-underline transition hover:bg-accent-700">Sign up</a>
              </div>
            </div>
          )}

          <Group>
            <DarkSwitch />
            {items('preferences')}
          </Group>

          <Group>
            {items('help')}
            {user && (
              <button type="button" role="menuitem" onClick={() => { setOpen(false); void signOut(); }} className={ITEM}><Glyph d={G.out} />Sign out</button>
            )}
          </Group>
        </div>
      )}

    </div>
  );
}
